/**
 * The phases a session moves through, and the events that move it.
 *
 * Pure data and pure functions — no renderer, no physics, no React. Everything here is
 * unit-testable without a GPU, which is the point of `src/game/` (see CLAUDE.md).
 */

export type Phase = 'loading' | 'playing' | 'crossing' | 'complete' | 'paused' | 'unsupported';

export type PhaseEvent =
  /** Assets and the physics world are ready. */
  | 'LOADED'
  /** The browser cannot run the game — no WebGL. Terminal. */
  | 'UNSUPPORTED'
  /** Menu opened, pointer lock lost, device turned to portrait, app backgrounded. */
  | 'PAUSE'
  /** The player asked to continue. */
  | 'RESUME'
  /** The player walked into a gate. The world holds still while it swaps underneath. */
  | 'CROSS'
  /** The level on the far side is built and the player is standing in it. */
  | 'ARRIVED'
  /** The last exit: the run is over. */
  | 'FINISH'
  /** Start the run again from the first level. */
  | 'RESTART';

export const INITIAL_PHASE: Phase = 'loading';

/**
 * Transitions that exist. Anything not listed is ignored rather than throwing: events
 * arrive from browser callbacks (visibility, orientation, pointer lock) that can fire in
 * any order and more than once, and a session should never die because two of them raced.
 */
const TRANSITIONS: Readonly<Record<Phase, Partial<Record<PhaseEvent, Phase>>>> = {
  loading: { LOADED: 'playing', UNSUPPORTED: 'unsupported' },
  playing: { PAUSE: 'paused', UNSUPPORTED: 'unsupported', CROSS: 'crossing', FINISH: 'complete' },
  // No PAUSE out of a crossing: it lasts a few hundred milliseconds, nothing is running
  // while it does, and a half-swapped world is not a thing to pause. A backgrounded tab
  // simply finds it still going on the way back, which is right.
  crossing: { ARRIVED: 'playing', UNSUPPORTED: 'unsupported' },
  complete: { RESTART: 'playing', UNSUPPORTED: 'unsupported' },
  paused: { RESUME: 'playing' },
  unsupported: {},
};

export function applyPhaseEvent(phase: Phase, event: PhaseEvent): Phase {
  return TRANSITIONS[phase][event] ?? phase;
}

/** Whether game input and the physics step should be running. */
export function isPlayable(phase: Phase): boolean {
  return phase === 'playing';
}
